import { useRouter } from 'next/router';
import { FC, useState } from 'react';

import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import { JumpNavConfig } from 'components/JumpNav/config/config';

type JumpNavProps = JumpNavConfig;

const JumpNav: FC<JumpNavProps> = props => {
  const {
    sectionProps,
    rest: { label = 'Jump to', items = [] },
  } = splitSectionProps(props);

  const router = useRouter();

  // Deliberately component-local. This used to live in a zustand store keyed by
  // `router.pathname`, which is `/[[...slug]]` for every Builder page, so the
  // active index leaked between pages and the old mount effect scrolled
  // visitors into the middle of whatever page they had just navigated to.
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToHref = (href: string) => {
    if (href.startsWith('#')) {
      const target = document.querySelector<HTMLElement>(href);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });

        return true;
      }
    }

    return false;
  };

  const handleClick = (item: JumpNavProps['items'][number], index: number) => {
    setActiveIndex(index);

    const href = item.href;
    if (!href) {
      return;
    }

    // Misma página, anchor
    if (href.startsWith('#')) {
      if (!scrollToHref(href)) {
        window.location.hash = href;
      }

      return;
    }

    // Ruta interna de Next
    if (href.startsWith('/')) {
      void router.push(href, undefined, { shallow: true, scroll: true });

      return;
    }

    // Externa
    window.location.href = href;
  };

  const isDark = sectionProps.darkMode ?? true;

  const labelColor = isDark ? 'text-white/90' : 'text-gray-900';
  const barBg = isDark ? 'bg-white/5 border-white/30' : 'bg-primary-50 border-primary-1400';

  const activeTabBg = isDark
    ? 'bg-white/10 text-white shadow-[0px_24px_48px_-12px_rgba(42,147,152,0.18)]'
    : 'bg-[linear-gradient(180deg,#E2FAFA_0%,#99E8EB_100%)] shadow-tocTabBoxShadow text-gray-900';

  const inactiveTabText = isDark ? 'text-gray-300 hover:text-primary-1400' : 'text-gray-700 hover:text-gray-900';

  const outlineActive = isDark
    ? 'outline outline-[1.5px] outline-offset-[-0.75px] outline-[#23b9c2]'
    : 'outline outline-[1.5px] outline-offset-[-0.75px] outline-[#23b9c2]';

  if (!items.length) {
    return <Section {...sectionProps} />;
  }

  return (
    <Section {...sectionProps}>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-16">
        {label && (
          <div className={`shrink-0 text-center text-[20px] font-medium leading-[30px] lg:text-left ${labelColor}`}>
            {label}
          </div>
        )}
        <nav className="w-full" aria-label={label || 'Jump navigation'}>
          <div
            className={`
              grid w-full grid-cols-2 gap-2 rounded-lg border p-2 backdrop-blur-md
              md:grid-cols-3 md:gap-3 md:p-3
              lg:flex lg:gap-0 lg:p-0
              ${barBg}
            `}
          >
            {items.map((item, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  key={item.href || item.label}
                  type="button"
                  onClick={() => handleClick(item, index)}
                  className={`
                    flex cursor-pointer flex-col items-center justify-center
                    rounded-lg outline-none
                    lg:flex-1 lg:rounded-none
                    ${isActive ? activeTabBg : ''}
                  `}
                >
                  <span
                    className={`
                      whitespace-nowrap p-3 text-center text-sm font-semibold leading-tight
                      lg:px-6 lg:text-[18px] lg:leading-[28px]
                      ${isActive ? '' : inactiveTabText}
                    `}
                  >
                    {item.label}
                  </span>
                  <span
                    className={`
                      hidden h-0 w-full lg:block
                      ${isActive ? outlineActive : 'outline-0 outline-transparent'}
                    `}
                  />
                </button>
              );
            })}
          </div>
        </nav>
      </div>
    </Section>
  );
};

export default JumpNav;
